import {GraphQlField, GraphQlType} from '../../types/GraphQlSchema';
import {Maybe} from '../../types/Maybe';
import {ObjectDetails, ObjectInfo, ObjectPropertyDetails} from '../../types/Object';

const DEFAULT_FIELD_RANGE = "String";

/**
 * Converts the info response to a {@link ObjectInfo} structure.
 * @param response the response to convert
 *
 * @returns {ObjectInfo}
 */
export const mapInfoResponse = (response: any): ObjectInfo => {
  const objectResponses: any = Object.values(response)[0];
  const objectResponse = objectResponses[0];

  return {
    id: objectResponse['id'],
    type: objectResponse['__typename']
  };
};

/**
 * Converts the details response to a {@link ObjectDetails} structure.
 * 
 * @param response the response to convert
 * @param {GraphQlType} type the GraphQL type of the response object
 * @returns {ObjectDetails}
 */
export const mapDetailsResponse = (response: any, type: GraphQlType): ObjectDetails => {
  const objectResponses: any = Object.values(response)[0];
  const objectResponse = objectResponses[0];

  const propertiesMap: Record<string, ObjectPropertyDetails> = {};
  Object.keys(objectResponse).forEach((name: string) => {
    const gqlField: Maybe<GraphQlField> = type.fieldsMap.get(name);
    if (gqlField) {
      propertiesMap[name] = {
        name: name,
        type: gqlField.range || DEFAULT_FIELD_RANGE,
        value: objectResponse[name],
        multi: gqlField.isMulti || (objectResponse[name] instanceof Array),
        graphQlFieldRef: gqlField
      };
    } else {
      console.error(`Response field ${name} is missing from the GraphQL type!`);
    }
  });

  return {
    id: propertiesMap['id'].value,
    type: type.name,
    properties: transformProperties(propertiesMap),
    propertiesMap,
    graphQlTypeRef: type
  };
};

/**
 * Converts the response of a more objects query.
 * 
 * @param response the response to convert
 * @param {GraphQlField} field the field for which more objects were loaded
 * @returns {Array<any>}
 */
export const mapMoreObjectsResponse = (response: any, field: GraphQlField): Array<any> => {
  const objectResponses: any = Object.values(response)[0];
  const objectResponse = objectResponses[0];
  // Returning raw response as the object properties are not mapped to any type
  return objectResponse[field.name] || [];
};

function transformProperties(propertiesMap: Record<string, ObjectPropertyDetails>) {
  const properties: Array<ObjectPropertyDetails> = Object.values(propertiesMap);
  properties.forEach((property: ObjectPropertyDetails) => {
    if (property.multi) {
      removeNullValues(property);
    }
    if (property.type === "Literal") {
      sortLiterals(property);
    }
  });
  return properties;
}

function removeNullValues(property: ObjectPropertyDetails) {
  property.value = property.value.filter((value: any) => value !== undefined && value !== null);
}

function sortLiterals(property: ObjectPropertyDetails) {
  if (property.value instanceof Array) {
    property.value.sort((left, right) => {
      if (left.lang) {
        return left.lang.localeCompare(right.lang);
      }
      else if (right.right) {
        return right.lang.localeCompare(left.lang);
      }
      return 0;
    });
  }
}
